import { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useInViewport from "@/hooks/useInViewport";

/**
 * CenteredCarousel — shared sliding-card carousel used across the homepage.
 * One card per slide on mobile, configurable on tablet / desktop.
 * Controls: gray circular prev/next buttons flanking a centered dot row
 * with an emerald active-dot pill. Auto-rotates, pauses on hover + when
 * the tab is hidden, swipe-enabled on mobile.
 *
 * Props:
 *   items      — array of items to render (one per slide)
 *   renderItem — (item, index) => JSX returned for each slide cell
 *   perViewMd  — number of cards visible at ≥768px (default 2)
 *   perViewLg  — number of cards visible at ≥1024px (default 3)
 *   perViewXl  — number of cards visible at ≥1280px (default = perViewLg)
 *   intervalMs — autoplay interval (default 6500)
 *   testid     — root data-testid; child slide testids derive from it
 */
export function CenteredCarousel({
  items,
  renderItem,
  perViewMd = 2,
  perViewLg = 3,
  perViewXl,
  intervalMs = 6500,
  testid = "carousel",
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [perView, setPerView] = useState(1);
  const [rootRef, inView] = useInViewport(0.2);
  const total = items.length;

  useEffect(() => {
    const calc = () => {
      if (typeof window === "undefined") return;
      const w = window.innerWidth;
      setPerView(w >= 1280 ? (perViewXl ?? perViewLg) : w >= 1024 ? perViewLg : w >= 768 ? perViewMd : 1);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [perViewMd, perViewLg, perViewXl]);

  useEffect(() => {
    if (paused || !inView || total <= perView) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % total), intervalMs);
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [paused, inView, total, perView, intervalMs]);

  const touch = useRef({ startX: 0, dx: 0 }).current;
  const onTouchStart = (e) => { touch.startX = e.touches[0].clientX; touch.dx = 0; };
  const onTouchMove  = (e) => { touch.dx = e.touches[0].clientX - touch.startX; };
  const onTouchEnd   = () => {
    if (Math.abs(touch.dx) < 40) return;
    setIndex((i) => (touch.dx < 0 ? (i + 1) % total : (i - 1 + total) % total));
  };

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);
  const visibleCount = Math.min(perView, total);
  const visibleItems = Array.from({ length: visibleCount }, (_, offset) => (index + offset) % total);
  if (!total) return null;

  return (
    <div
      ref={rootRef}
      className="relative min-w-0 w-full max-w-full"
      data-testid={testid}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="overflow-hidden" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${visibleCount}, minmax(0, 1fr))` }}>
          {visibleItems.map((i) => (
            <div key={i} className="min-w-0" data-testid={`${testid}-card-${i}`}>
              {renderItem(items[i], i)}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-7 flex items-center justify-center gap-3 sm:gap-5">
        <button type="button" onClick={prev} aria-label="Previous" data-testid={`${testid}-prev`}
          className="w-11 h-11 shrink-0 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-700 flex items-center justify-center transition-colors shadow-sm">
          <ChevronLeft className="w-5 h-5"/>
        </button>
        <div className="flex min-w-0 flex-wrap justify-center items-center gap-2" data-testid={`${testid}-dots`}>
          {items.map((_, i) => {
            const active = i === index;
            return (
              <button key={i} type="button" onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`} data-testid={`${testid}-dot-${i}`}
                className={`h-2.5 rounded-full transition-all ${active ? "w-7 bg-emerald-600" : "w-2.5 bg-slate-300 hover:bg-slate-400"}`} />
            );
          })}
        </div>
        <button type="button" onClick={next} aria-label="Next" data-testid={`${testid}-next`}
          className="w-11 h-11 shrink-0 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-700 flex items-center justify-center transition-colors shadow-sm">
          <ChevronRight className="w-5 h-5"/>
        </button>
      </div>
    </div>
  );
}
